<template>
  <section class="base-carousel" @focusin="onFocusIn" @focusout="onFocusOut" @keydown="onKeyDown">
    <div class="base-carousel__container container">
      <div class="base-carousel__header">
        <div class="base-carousel__header-text">
          <slot name="header" />
        </div>
        <div :id="`base-carousel__nav-target__${uid}`"></div>
      </div>

      <slot v-if="isError" name="error" />
      <slot v-else-if="!hasItems && !isLoading" name="empty" />
    </div>

    <div ref="swiperContainerRef" class="base-carousel__content container">
      <template v-if="isLoading">
        <span v-if="srLoadingText" class="sr-only" role="alert" aria-live="polite">
          {{ srLoadingText }}
        </span>
        <slot name="loading" />
      </template>

      <template v-else-if="hasItems">
        <span v-if="srLoadedText" class="sr-only" role="alert" aria-live="polite">
          {{ srLoadedText }}
        </span>

        <div
          v-if="!isServer() && showSwiper"
          class="base-carousel__swiper"
          :style="
            stretchToEdge
              ? {
                  '--swiper-width': `calc(100% + ${marginRight}px)`,
                }
              : {}
          "
        >
          <swiper
            :modules="modules"
            :navigation="navigationConfig"
            :a11y="a11yConfig"
            :space-between="16"
            :breakpoints="breakpoints"
            @init="onSwiperInit"
            v-on="stretchToEdge ? { resize: onSwiperResize } : {}"
          >
            <swiper-slide v-for="(item, index) in items" :key="itemKey(item, index)" tabindex="-1">
              <slot :item="item" :index="index" />
            </swiper-slide>
          </swiper>
        </div>

        <div v-else class="base-carousel__items">
          <div
            v-for="(item, index) in items"
            :key="itemKey(item, index)"
            class="base-carousel__item-wrapper"
          >
            <slot :item="item" :index="index" />
          </div>
        </div>
      </template>
    </div>

    <div class="base-carousel__container container">
      <template v-if="isMounted && showNavigation">
        <Teleport :disabled="isMobile" :to="`#base-carousel__nav-target__${uid}`">
          <div class="base-carousel__nav">
            <base-button
              :id="`base-carousel__nav-prev__${uid}`"
              rounded
              disabled
              icon="icon-Back"
              :aria-label="t('slider-previous-button')"
              @click="onNavClick"
            />
            <base-button
              :id="`base-carousel__nav-next__${uid}`"
              rounded
              icon="icon-Forward"
              :aria-label="t('slider-next-button')"
              @click="onNavClick"
            />
          </div>
        </Teleport>
      </template>
      <slot name="footer" />
    </div>
  </section>
</template>

<script setup lang="ts" generic="T">
import { computed, nextTick, ref, onMounted, useId, useTemplateRef } from 'vue';
import { isServer } from '@sitecore-jss/sitecore-jss/utils';
import { Navigation, A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { useI18n } from 'vue-i18n';
import BaseButton from '../BaseButton/BaseButton.vue';
import useMedia from '../../../utils/useMedia';
import { useCarouselTracking } from '../../../data-layer/hooks/useCarouselTracking';
import type { BaseCarouselProps } from './types';
import type { Swiper as SwiperType } from 'swiper';
import type { NavigationOptions, A11yOptions } from 'swiper/types';
import 'swiper/css';
import 'swiper/css/a11y';

const modules = [Navigation, A11y];

const { t } = useI18n();

const uid = useId();

const { isMedia: isMobile } = useMedia('(max-width: 767px)');

const props = withDefaults(defineProps<BaseCarouselProps<T>>(), {
  items: () => [],
  isLoading: false,
  isError: false,
  breakpoints: () => ({}),
  itemKey: (item, i: number) => ((item as Record<string, unknown>)?.id as string | number) ?? i,
  srLoadingText: '',
  srLoadedText: '',
  stretchToEdge: false,
});

const swiperRef = ref<SwiperType | null>(null);
const isMounted = ref(false);
const isActiveKeyboardNavigation = ref(false);
const marginRight = ref(0);

const swiperContainerRef = useTemplateRef('swiperContainerRef');

const minSlidesPerView = computed(() => {
  const first = Object.keys(props.breakpoints)
    .map(Number)
    .sort((a, b) => a - b)[0];
  const slidesPerView = props.breakpoints[first]?.slidesPerView ?? 1;
  return typeof slidesPerView === 'number' ? Math.floor(slidesPerView) : 1;
});

const hasItems = computed(() => props.items.length > 0);

const showSwiper = computed(() => props.items.length > minSlidesPerView.value);

const showNavigation = computed(() => !props.isLoading && hasItems.value && showSwiper.value);

const navigationConfig = computed<NavigationOptions>(() => ({
  prevEl: `#base-carousel__nav-prev__${uid}`,
  nextEl: `#base-carousel__nav-next__${uid}`,
}));

const { trackNextSlide, trackPrevSlide } = useCarouselTracking(
  { componentName: props.trackingComponentName },
  swiperRef
);

const onSwiperInit = (swiper: SwiperType): void => {
  nextTick(() => {
    swiper.navigation?.enable();
  });
  swiperRef.value = swiper;
  if (props.stretchToEdge) onSwiperResize();
};

const onSwiperResize = () => {
  if (!swiperRef.value) return;
  const newValue = Math.floor(
    Math.max(0, (window.innerWidth - (swiperContainerRef.value?.clientWidth ?? 0)) / 2)
  );
  if (Math.abs(newValue - marginRight.value) > 1) {
    marginRight.value = newValue;
  }
};

const a11yConfig: A11yOptions = {
  slideLabelMessage: '{{index}} of {{slidesLength}}',
  scrollOnFocus: true,
};

const onNavClick = () => {
  setTimeout(() => {
    swiperRef.value?.slides?.[swiperRef.value?.activeIndex]?.focus({ preventScroll: true });
  }, 0);
};

const onFocusIn = () => {
  isActiveKeyboardNavigation.value = true;
};

const onFocusOut = () => {
  isActiveKeyboardNavigation.value = false;
};

const onKeyDown = (e: KeyboardEvent): void => {
  if (!isActiveKeyboardNavigation.value) return;
  if (e.code === 'ArrowLeft') {
    swiperRef.value?.slidePrev();
    trackPrevSlide();
  }
  if (e.code === 'ArrowRight') {
    swiperRef.value?.slideNext();
    trackNextSlide();
  }
};

onMounted(() => {
  isMounted.value = true;
});
</script>

<style lang="scss">
@import './scss/BaseCarousel.scss';
</style>
